/**
 * Sensible markdown → plaintext for the assistant "Copy" action. The assistant's response arrives as
 * markdown text parts; joining them then stripping the common syntax (headings, emphasis, links, code
 * fences, list markers) yields readable plaintext without pulling in a markdown parser dependency.
 */

export function markdownToPlainText(markdown: string): string {
  let text = markdown
  // Fenced code blocks: keep the code, drop the ``` fences and the optional language hint.
  text = text.replace(/```[^\n]*\n?([\s\S]*?)```/g, (_match, code: string) => code)
  // Images before links (image syntax is a superset): keep the alt text / link text.
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
  // Inline code.
  text = text.replace(/`([^`]+)`/g, "$1")
  // Emphasis (bold before italic so `**x**` doesn't leave a stray `*`) and strikethrough. `_`/`__` only
  // delimit emphasis at word boundaries (CommonMark), so intraword underscores (portfolio_long_only) are
  // kept — matching the rendered output, which no longer treats them as emphasis either.
  text = text.replace(/\*\*(.*?)\*\*/g, "$1")
  text = text.replace(/(?<![\p{L}\p{N}_])__(.*?)__(?![\p{L}\p{N}_])/gu, "$1")
  text = text.replace(/\*(.*?)\*/g, "$1")
  text = text.replace(/(?<![\p{L}\p{N}_])_(.*?)_(?![\p{L}\p{N}_])/gu, "$1")
  text = text.replace(/~~(.*?)~~/g, "$1")
  // Line-leading syntax: horizontal rules drop entirely; headings / blockquotes / list markers lose the marker.
  text = text
    .split("\n")
    .map((line) => {
      if (/^\s*([-*_])\1{2,}\s*$/.test(line)) return ""
      return line
        .replace(/^\s{0,3}#{1,6}\s+/, "")
        .replace(/^\s{0,3}>\s?/, "")
        .replace(/^\s*[-*+]\s+/, "")
        .replace(/^\s*\d+\.\s+/, "")
    })
    .join("\n")
  // Collapse the blank-line runs that stripping can open up.
  return text.replace(/\n{3,}/g, "\n\n").trim()
}
